import { useEffect, useId, useMemo, useState, type RefObject } from 'react';
import { CheckCircle2 } from 'lucide-react';
import type { IssueQuickTag, IssueType } from '@ucp/web/lib';
// Import runtime constants from the source module, NOT the @ucp/web/lib barrel:
// the barrel's `export * as db` pulls node-only `pg` into the browser bundle.
import { ISSUE_EXTRA_DETAILS_MAX, ISSUE_TEXT_MAX } from '../../lib/types/issueReport';
import { Sheet } from '../ui/Sheet';
import { Field } from '../ui/Field';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Checkbox } from '../ui/Checkbox';
import { Button } from '../ui/Button';
import { RadioGroup } from '../ui/RadioGroup';
import { useReportIssue, type ReportIssueFields } from '../../hooks/useReportIssue';

/**
 * Send feedback capture modal (PRO-0550 / PRO-1560). Dual-mode: a problem report
 * (existing fields) or an improvement. Opened from the header AccountMenu.
 * Technical context is auto-captured at submit. Free-text is NEVER persisted
 * client-side; last-used kind + problem type (non-PII) may be remembered.
 */

interface ReportIssueDialogProps {
  open: boolean;
  onClose: () => void;
  /** Focus returns here on close — the AccountMenu trigger. */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

type FeedbackKind = 'problem' | 'improvement';

const ISSUE_TYPE_OPTIONS: ReadonlyArray<{ value: IssueType; label: string }> = [
  { value: 'bug', label: 'Something is broken (a bug)' },
  { value: 'data_issue', label: 'Data looks wrong' },
  { value: 'access_problem', label: "I can't access something" },
  { value: 'performance', label: 'It is slow or unresponsive' },
  { value: 'other', label: 'Something else' },
];

const QUICK_TAG_OPTIONS: ReadonlyArray<{ value: IssueQuickTag; label: string }> = [
  { value: 'did_not_load', label: "The page didn't load" },
  { value: 'frozen', label: 'It froze or stopped responding' },
  { value: 'error_message', label: 'I saw an error message' },
  { value: 'wrong_data', label: 'The wrong data was shown' },
];

const LAST_TYPE_KEY = 'ucp.reportIssue.lastType';
const LAST_KIND_KEY = 'ucp.feedback.lastKind';

function readLastType(): IssueType {
  try {
    const stored = window.localStorage.getItem(LAST_TYPE_KEY);
    if (stored && ISSUE_TYPE_OPTIONS.some((o) => o.value === stored)) {
      return stored as IssueType;
    }
  } catch {
    // localStorage unavailable (private mode / disabled) — fall through to default.
  }
  return 'bug';
}

function readLastKind(): FeedbackKind {
  try {
    const stored = window.localStorage.getItem(LAST_KIND_KEY);
    if (stored === 'problem' || stored === 'improvement') {
      return stored;
    }
  } catch {
    // Non-fatal.
  }
  return 'problem';
}

export function ReportIssueDialog({ open, onClose, returnFocusRef }: ReportIssueDialogProps) {
  const titleId = useId();
  const { state, submit, reset } = useReportIssue();

  const [kind, setKind] = useState<FeedbackKind>('problem');
  const [issueType, setIssueType] = useState<IssueType>('bug');
  const [tried, setTried] = useState('');
  const [happened, setHappened] = useState('');
  const [quickTags, setQuickTags] = useState<IssueQuickTag[]>([]);
  const [extraDetails, setExtraDetails] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (open) {
      setKind(readLastKind());
      setIssueType(readLastType());
      setTried('');
      setHappened('');
      setQuickTags([]);
      setExtraDetails('');
      setShowValidation(false);
      reset();
    }
  }, [open, reset]);

  const submitting = state.status === 'submitting';
  const isImprovement = kind === 'improvement';
  const triedError =
    showValidation && tried.trim().length === 0
      ? isImprovement
        ? 'Please tell us what you would like to change or add.'
        : 'Please tell us what you were doing.'
      : undefined;
  const happenedError =
    showValidation && happened.trim().length === 0
      ? isImprovement
        ? 'Please tell us why that would help.'
        : 'Please tell us what happened.'
      : undefined;

  const toggleTag = (tag: IssueQuickTag, checked: boolean) => {
    setQuickTags((prev) => (checked ? [...new Set([...prev, tag])] : prev.filter((t) => t !== tag)));
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (tried.trim().length === 0 || happened.trim().length === 0) {
      setShowValidation(true);
      return;
    }
    try {
      window.localStorage.setItem(LAST_KIND_KEY, kind);
      if (!isImprovement) {
        window.localStorage.setItem(LAST_TYPE_KEY, issueType);
      }
    } catch {
      // Non-fatal — remembering the last type is a convenience only.
    }
    const fields: ReportIssueFields = {
      issueType: isImprovement ? 'improvement' : issueType,
      tried: tried.trim(),
      happened: happened.trim(),
      quickTags: isImprovement ? [] : quickTags,
      extraDetails,
    };
    void submit(fields);
  };

  const success = state.status === 'success' ? state : null;

  const tagListId = useMemo(() => `${titleId}-tags`, [titleId]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      {...(returnFocusRef ? { returnFocusRef } : {})}
      anchor="center"
      id="report-issue-dialog"
      aria-labelledby={titleId}
    >
      <div className="flex max-h-[85vh] w-full flex-col">
        <div className="border-b border-border px-5 py-4">
          <h2 id={titleId} className="text-lg font-semibold text-text-primary">
            Send feedback
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Tell us about a problem or an improvement. It goes to the platform team.
          </p>
        </div>

        {success ? (
          <div className="flex flex-1 flex-col items-center gap-3 overflow-y-auto px-5 py-8 text-center">
            <CheckCircle2 aria-hidden className="h-10 w-10 text-success" />
            <p className="text-base font-semibold text-text-primary">Thanks — your feedback was sent.</p>
            <p className="text-sm text-text-secondary">
              Reference <span className="font-mono font-medium text-text-primary">{success.reference}</span>.
              The platform team has been notified.
            </p>
            <Button variant="primary" onClick={onClose} className="mt-2">
              Done
            </Button>
          </div>
        ) : (
          <form className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4" onSubmit={onSubmit} noValidate>
            <RadioGroup
              name="feedback-kind"
              legend="What would you like to send?"
              ariaLabel="Feedback kind"
              value={kind}
              onChange={setKind}
              options={[
                { value: 'problem', label: 'A problem', description: 'Something is broken or looks wrong' },
                { value: 'improvement', label: 'An improvement', description: 'A change or addition you would like' },
              ]}
            />

            {isImprovement ? null : (
              <Select
                id="issue-type"
                label="What kind of issue is it?"
                value={issueType}
                onChange={(e) => setIssueType(e.target.value as IssueType)}
                disabled={submitting}
                required
              >
                {ISSUE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}

            <Textarea
              id="issue-tried"
              label={isImprovement ? 'What would you like to change or add?' : 'What were you trying to do?'}
              value={tried}
              onChange={(e) => setTried(e.target.value)}
              rows={3}
              maxLength={ISSUE_TEXT_MAX}
              disabled={submitting}
              required
              error={triedError}
            />

            <Textarea
              id="issue-happened"
              label={isImprovement ? 'Why would that help?' : 'What happened instead?'}
              value={happened}
              onChange={(e) => setHappened(e.target.value)}
              rows={3}
              maxLength={ISSUE_TEXT_MAX}
              disabled={submitting}
              required
              error={happenedError}
            />

            {isImprovement ? null : (
              <fieldset className="flex flex-col gap-1" aria-describedby={tagListId}>
                <legend className="text-sm font-medium text-text-primary">
                  Anything that describes it? <span className="text-text-secondary">(optional)</span>
                </legend>
                <p id={tagListId} className="sr-only">
                  Select any that apply.
                </p>
                <div className="flex flex-col">
                  {QUICK_TAG_OPTIONS.map((o) => (
                    <Checkbox
                      key={o.value}
                      label={o.label}
                      checked={quickTags.includes(o.value)}
                      onChange={(e) => toggleTag(o.value, e.target.checked)}
                      disabled={submitting}
                    />
                  ))}
                </div>
              </fieldset>
            )}

            <Field
              htmlFor="issue-extra"
              label="Anything else that might help?"
              description="Optional. Avoid including personal details you don't need to share."
            >
              <Textarea
                id="issue-extra"
                value={extraDetails}
                onChange={(e) => setExtraDetails(e.target.value)}
                rows={2}
                maxLength={ISSUE_EXTRA_DETAILS_MAX}
                disabled={submitting}
                aria-describedby="issue-extra-description"
              />
            </Field>

            {state.status === 'error' ? (
              <p role="alert" className="text-sm text-danger">
                {state.message}
              </p>
            ) : null}

            <div className="mt-1 flex items-center justify-end gap-2">
              <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send feedback'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Sheet>
  );
}
